<template>
    <div>
        <rkm-timeline>
            <rkm-timeline-item
                v-for="(item, i) in data.additionals.timeline"
                :key="i"
                :dateTime="item.dateTime"
                :title="item.title"
                :label="item.label"
                :disabled="item.disabled"
                :line-class="getTimeLineClass(item, i)"
            />
        </rkm-timeline>

        <rkm-form ref="RkmForm">
            <rkm-alert>
                <strong>Data do atendimento:</strong> {{data.model.dataAtendimento}}
            </rkm-alert>

            <rkm-row>
                <rkm-col>
                    <box-paciente :data="data" />
                </rkm-col>

                <rkm-col v-if="data.form.historicoAtendimento.length > 0">
                    <box-historico-atendimento :data="data" />
                </rkm-col>
            </rkm-row>

            <box-paciente-observacao
                v-if="showBoxPacienteObservacao"
                :data="data"
            />

            <box-classificacao-risco :data="data" />

            <box-fator-risco :data="data" />

            <box-alergia
                :data="data"
            />

            <box-medicamento-uso-continuo
                :data="data"
            />

            <box-medicamento-uso
                :data="data"
            />

            <box-sinal-vital :data="data" />

            <box-escala-coma-glasgow
                v-if="!showBoxPacienteObservacao"
                :data="data"
                readonly
            />

            <rkm-row>
                <rkm-col :cols="showBoxGestante ? 7 : 12">
                    <box-avaliacao-antropometrica :data="data" />
                </rkm-col>

                <rkm-col
                    v-if="showBoxGestante"
                    cols="5"
                    id="gestante"
                >
                    <box-gestante :data="data" />
                </rkm-col>
            </rkm-row>

            <box-atendimento-enfermagem
                :data="data"
                :readonly="true"
                :visible="false"
                :procedimento="true"
            />

            <box-anotacoes-enfermagem
                :data="data"
                :hidden="true"
                funcionalidade="atendimento-enfermagem"
            />

            <rkm-area title="ATENDIMENTO MÉDICO">
                <rkm-row v-if="data.form.reavaliacao">
                    <rkm-col>
                        <rkm-form-incremental-textarea
                            :items="data.form.evolucao"
                            :validations="validations.evolucao"
                            label="Evolução Médica"
                            v-model="data.model.evolucao"
                            start-visible
                        >
                            <template v-slot:row="{ item }">
                                <rkm-row>
                                    <rkm-col cols="4">
                                        <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                        <div>{{ item.lotacao.profissional.nome }}</div>
                                        <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                        <div>{{ item.dataHora }}</div>
                                    </rkm-col>

                                    <rkm-col>
                                        {{ item.descricao }}
                                    </rkm-col>
                                </rkm-row>
                            </template>
                        </rkm-form-incremental-textarea>
                    </rkm-col>
                </rkm-row>

                <div v-if="data.form.tipoAtendimentoId === enums.tipoAtendimento.ESTRUTURADO">
                    <rkm-row>
                        <rkm-col>
                            <rkm-form-incremental-textarea
                                :items="data.form.queixaPrincipalDuracao"
                                :validations="validations.atendimento.queixaPrincipalDuracao"
                                label="Queixa Principal e Duração"
                                v-model="data.model.queixaPrincipalDuracao"
                            >
                                <template v-slot:row="{ item }">
                                    <rkm-row>
                                        <rkm-col cols="4">
                                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                            <div>{{ item.lotacao.profissional.nome }}</div>
                                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                            <div>{{ item.dataHora }}</div>
                                        </rkm-col>

                                        <rkm-col>
                                            {{ item.descricao }}
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-form-incremental-textarea>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-incremental-textarea
                                :items="data.form.historia"
                                :validations="validations.atendimento.historia"
                                label="História do Paciente"
                                v-model="data.model.historia"
                            >
                                <template v-slot:row="{ item }">
                                    <rkm-row>
                                        <rkm-col cols="4">
                                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                            <div>{{ item.lotacao.profissional.nome }}</div>
                                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                            <div>{{ item.dataHora }}</div>
                                        </rkm-col>

                                        <rkm-col>
                                            {{ item.descricao }}
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-form-incremental-textarea>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-incremental-textarea
                                :items="data.form.exameFisico"
                                :validations="validations.atendimento.exameFisico"
                                label="Exame Físico"
                                v-model="data.model.exameFisico"
                            >
                                <template v-slot:row="{ item }">
                                    <rkm-row>
                                        <rkm-col cols="4">
                                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                            <div>{{ item.lotacao.profissional.nome }}</div>
                                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                            <div>{{ item.dataHora }}</div>
                                        </rkm-col>

                                        <rkm-col>
                                            {{ item.descricao }}
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-form-incremental-textarea>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-incremental-textarea
                                :items="data.form.diagnostico"
                                :validations="validations.atendimento.diagnostico"
                                label="Diagnóstico"
                                v-model="data.model.diagnostico"
                            >
                                <template v-slot:row="{ item }">
                                    <rkm-row>
                                        <rkm-col cols="4">
                                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                            <div>{{ item.lotacao.profissional.nome }}</div>
                                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                            <div>{{ item.dataHora }}</div>
                                        </rkm-col>

                                        <rkm-col>
                                            {{ item.descricao }}
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-form-incremental-textarea>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-incremental-textarea
                                :items="data.form.conduta"
                                :validations="validations.atendimento.conduta"
                                label="Conduta"
                                v-model="data.model.conduta"
                            >
                                <template v-slot:row="{ item }">
                                    <rkm-row>
                                        <rkm-col cols="4">
                                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                            <div>{{ item.lotacao.profissional.nome }}</div>
                                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                            <div>{{ item.dataHora }}</div>
                                        </rkm-col>

                                        <rkm-col>
                                            {{ item.descricao }}
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-form-incremental-textarea>
                        </rkm-col>
                    </rkm-row>
                </div>

                <div v-else>
                    <rkm-row>
                        <rkm-col>
                            <rkm-form-incremental-textarea
                                :items="data.form.atendimentoGeral"
                                :validations="validations.atendimento.atendimentoGeral"
                                label="Anamnese"
                                v-model="data.model.atendimentoGeral"
                            >
                                <template v-slot:row="{ item }">
                                    <rkm-row>
                                        <rkm-col cols="4">
                                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                            <div>{{ item.lotacao.profissional.nome }}</div>
                                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                            <div>{{ item.dataHora }}</div>
                                        </rkm-col>

                                        <rkm-col>
                                            {{ item.descricao }}
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-form-incremental-textarea>
                        </rkm-col>
                    </rkm-row>
                </div>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-incremental-textarea
                            :items="data.form.observacoesEnfermagem"
                            :validations="validations.atendimento.observacoesEnfermagem"
                            label="Observações de Serviços de Enfermagem"
                            v-model="data.model.observacoesEnfermagem"
                        >
                            <template v-slot:row="{ item }">
                                <rkm-row>
                                    <rkm-col cols="4">
                                        <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                        <div>{{ item.lotacao.profissional.nome }}</div>
                                        <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                        <div>{{ item.dataHora }}</div>
                                    </rkm-col>

                                    <rkm-col>
                                        {{ item.descricao }}
                                    </rkm-col>
                                </rkm-row>
                            </template>
                        </rkm-form-incremental-textarea>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-incremental-textarea
                            :items="data.form.observacoes"
                            :validations="validations.observacoes"
                            label="Observações"
                            v-model="data.model.observacoes"
                        >
                            <template v-slot:row="{ item }">
                                <rkm-row>
                                    <rkm-col cols="4">
                                        <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                        <div>{{ item.lotacao.profissional.nome }}</div>
                                        <div>{{ item.dataHora }}</div>
                                    </rkm-col>

                                    <rkm-col>
                                        {{ item.descricao }}
                                    </rkm-col>
                                </rkm-row>
                            </template>
                        </rkm-form-incremental-textarea>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <box-procedimento :data="data" />

            <box-cid :data="data" />

            <box-protocolo
                v-if="data.model.dorToracica"
                :data="data"
                :readonly="true"
                is-atendimento-medico
            />

            <rkm-area title="Setor de Encaminhamento">
                <rkm-row>
                    <rkm-col :cols="data.additionals.fromObservacao ? 12 : 8">
                        <rkm-form-select
                            :options="data.form.encaminhamentoSetores"
                            :validations="validations.centroCustoIdPara"
                            label="Setor"
                            text-field="nome"
                            v-model="data.model.centroCustoIdPara"
                            value-field="id"
                        >
                            <rkm-form-select-option
                                :value="null"
                                slot="first"
                            >
                                Selecione
                            </rkm-form-select-option>
                        </rkm-form-select>
                    </rkm-col>

                    <rkm-col v-if="!data.additionals.fromObservacao">
                        <rkm-form-radio-group
                            :validations="validations.solicitouReavaliacao"
                            @change="handleSolicitouReavaliacao()"
                            label="Reavaliação"
                            v-model="data.model.solicitouReavaliacao"
                        >
                            <rkm-form-radio :value="true">Sim</rkm-form-radio>
                            <rkm-form-radio :value="false">Não (Alta médica)</rkm-form-radio>
                        </rkm-form-radio-group>
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                v-if="showBoxDataHoraProximaReavaliacao"
                title="PRÓXIMA REAVALIAÇÃO"
            >
                <rkm-row>
                    <rkm-col
                        v-if="data.additionals.fromObservacao"
                        cols="auto"
                    >
                        <rkm-form-radio-group
                            :validations="validations.proximaReavaliacao"
                            :label="false"
                            v-model="data.model.proximaReavaliacao"
                        >
                            <rkm-form-radio :value="true">Sim</rkm-form-radio>
                            <rkm-form-radio :value="false">Não</rkm-form-radio>
                        </rkm-form-radio-group>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :validations="validations.dataProximaReavaliacao"
                            :min-date="data.form.dataAtual"
                            :max-date="data.form.dataMaxProximaReavaliacao"
                            label="Data"
                            v-model="data.model.dataProximaReavaliacao"
                            :disabled="data.model.proximaReavaliacao === false"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-time-picker
                            :validations="validations.horaProximaReavaliacao"
                            label="Hora"
                            v-model="data.model.horaProximaReavaliacao"
                            :disabled="data.model.proximaReavaliacao === false"
                            right
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-fab-area>
                <rkm-fab>

                    <rkm-fab-item
                        icon="plus"
                        label="Prescrição interna"
                        @click="$refs.ModalPrescricaoInterna.open()"
                        :color="data.model.prescricoesInternas.length !== 0 ||
                            data.form.prescricoesInternas.length !== 0 ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Solicitação de exames"
                        @click="$refs.ModalExameSadt.open()"
                        :color="data.model.pedidosExames.length !== 0 ||
                            data.form.pedidosExames.length !== 0 ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Serviço de enfermagem"
                        @click="$refs.ModalServicoEnfermagem.open()"
                        :color="data.model.servicosEnfermagem.length !== 0 ||
                            data.form.servicosEnfermagem.length !== 0 ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Receita médica"
                        @click="$refs.ModalReceitaMedica.open()"
                        :color="data.model.receitaMedica.medicamentos.length !== 0  ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Encaminhamento médico"
                        @click="$refs.ModalEncaminhamentoMedico.open()"
                        :color="data.model.encaminhamentos.length !== 0 ||
                            data.form.encaminhamentos.length !== 0 ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Orientação"
                        @click="$refs.ModalOrientacao.open()"
                        :color="data.model.orientacoes ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Atestado"
                        @click="$refs.ModalAtestado.open()"
                        :color="data.model.atestado.documentoTipoId ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        icon="plus"
                        label="Declaração de comparecimento"
                        @click="$refs.ModalDeclaracaoComparecimento.open()"
                        :color="data.model.declaracoesComparecimento.length !== 0 ||
                            data.form.declaracoesComparecimento.length !== 0 ? 'primary' : ''"
                    />

                    <rkm-fab-item
                        @click="$refs.ModalCondicaoSaudePaciente.open()"
                        icon="plus"
                        label="Condição de saúde do paciente"
                    />

                    <rkm-fab-item
                        @click="$refs.ModalHistoricoAmbulatorio.open()"
                        icon="plus"
                        label="Histórico completo"
                    />
                </rkm-fab>
            </rkm-fab-area>

            <modal-impressao ref="ModalImpressao" :from-observacao="data.additionals.fromObservacao" />
            <modal-atestado ref="ModalAtestado" :data="data" />
            <modal-declaracao-comparecimento ref="ModalDeclaracaoComparecimento" :data="data" />
            <modal-encaminhamento-medico ref="ModalEncaminhamentoMedico" :data="data" />
            <modal-exame-sadt ref="ModalExameSadt" :data="data" />
            <modal-orientacao ref="ModalOrientacao" :data="data" />

            <modal-prescricao-interna
                :data="data"
                @open-modal-receita-medica="$refs.ModalReceitaMedica.open()"
                ref="ModalPrescricaoInterna"
            />

            <modal-acidente-trabalho ref="ModalAcidenteTrabalho" :data="data" />

            <modal-receita-medica
                :data="data"
                @open-modal-prescricao-interna="$refs.ModalPrescricaoInterna.open()"
                ref="ModalReceitaMedica"
            />

            <modal-servico-enfermagem ref="ModalServicoEnfermagem" :data="data" />
        </rkm-form>

        <ModalAssinaturaEletronica
            :after-sign="afterSign"
            :ref="modalAssinaturaEletronicaRefName"
            @sign="onSign"
            @skip-sign="onSkipSign"
            v-bind="modalAssinaturaEletronicaFaaProps"
        />

        <ModalCondicaoSaudePaciente
            :data="data"
            ref="ModalCondicaoSaudePaciente"
        />

        <ModalHistoricoAmbulatorio
            :data="data"
            ref="ModalHistoricoAmbulatorio"
        />
    </div>
</template>

<script>
// Components.
import BoxAlergia from '@/components/cadastro/cidadao/components/alergia/Index'
import BoxMedicamentoUsoContinuo from '@/components/cadastro/cidadao/components/medicamento-uso-continuo/Index'
import BoxAnotacoesEnfermagem from '@/components/pronto-atendimento/components/anotacao-enfermagem/Index'
import BoxAtendimentoEnfermagem from '@/components/pronto-atendimento/components/enfermagem/Index'
import BoxAvaliacaoAntropometrica from '@/components/pronto-atendimento/components/avaliacao-antropometrica/Index'
import BoxCid from '@/components/pronto-atendimento/components/cid/Index'
import BoxClassificacaoRisco from '@/components/pronto-atendimento/components/classificacao-risco/Index'
import BoxEscalaComaGlasgow from '@/components/pronto-atendimento/components/escala-coma-glasgow/Index'
import BoxFatorRisco from '@/components/pronto-atendimento/components/fator-risco/Index'
import BoxGestante from '@/components/pronto-atendimento/components/gestante/Index'
import BoxHistoricoAtendimento from '@/components/pronto-atendimento/components/historico-atendimento/Index'
import BoxMedicamentoUso from '@/components/pronto-atendimento/components/medicamento-uso/Index'
import BoxPaciente from '@/components/pronto-atendimento/components/paciente/Index'
import BoxPacienteObservacao from '@/components/pronto-atendimento/observacao/Show'
import BoxProcedimento from '@/components/pronto-atendimento/components/procedimento/Index'
import BoxProtocolo from '@/components/pronto-atendimento/components/protocolo/Index'
import BoxSinalVital from '@/components/pronto-atendimento/components/sinal-vital/Index'
import ModalAtestado from '@/components/pronto-atendimento/atendimento-medico/components/atestado/Modal'
import ModalDeclaracaoComparecimento from '@/components/pronto-atendimento/atendimento-medico/components/declaracao-comparecimento/Modal'
import ModalEncaminhamentoMedico from '@/components/pronto-atendimento/atendimento-medico/components/encaminhamento/Modal'
import ModalExameSadt from '@/components/pronto-atendimento/atendimento-medico/components/exame-sadt/Modal'
import ModalImpressao from '@/components/pronto-atendimento/impressao/components/impressao/Modal'
import ModalOrientacao from '@/components/pronto-atendimento/atendimento-medico/components/orientacao/Modal'
import ModalPrescricaoInterna from '@/components/pronto-atendimento/atendimento-medico/components/prescricao-interna/Modal'
import ModalAcidenteTrabalho from '@/components/pronto-atendimento/components/acidente-trabalho/Modal'
import ModalReceitaMedica from '@/components/pronto-atendimento/atendimento-medico/components/receita-medica/Modal'
import ModalServicoEnfermagem from '@/components/pronto-atendimento/atendimento-medico/components/servico-enfermagem/Modal'
import RkmFormIncrementalTextarea from '@/components/pronto-atendimento/components/base/FormIncrementalTextarea'
import ModalCondicaoSaudePaciente from '@/components/pronto-atendimento/components/condicao-saude-paciente/Modal'
import ModalHistoricoAmbulatorio from '@/components/pronto-atendimento/components/historico-ambulatorio/Modal'

// Enums.
import desfecho from '@/enums/pronto-atendimento/desfecho'
import documentoTipo from '@/enums/cadastro/documentoTipo'
import setorTipo from '@/enums/cadastro/estabelecimento/setorTipo'
import sexo from '@/enums/cadastro/cidadao/sexo'
import tipoAplicacao from '@/enums/pronto-atendimento/receita/tipoAplicacao'
import tipoAtendimento from '@/enums/pronto-atendimento/parametrizacao/tipoAtendimento'
import tipoPosologia from '@/enums/pronto-atendimento/receita/tipoPosologia'
import trauma from '@/enums/pronto-atendimento/observacao/paciente-trauma'
import unidadeMedida from '@/enums/cadastro/unidadeMedida'
import viaAdministracao from '@/enums/cadastro/medicamento/viaAdministracao'
import tipoExame from '@/enums/pronto-atendimento/parametrizacao/tipoExame'
import tipoFormularioAcidenteTrabalho from '@/enums/pronto-atendimento/tipoFormularioAcidenteTrabalho'
import atestadoModelo from '@/enums/pronto-atendimento/atendimento-medico/atestadoModelo'

// Mixins.
import { error, repository, unmask, prontoAtendimentoAssinaturaEletronica } from '@/mixins'

// Others.
import moment from 'moment'

// Utils.
import string from '@/utils/stringCase'

// Vuex.
import { mapActions } from 'vuex'

export default {
    components: {
        BoxAlergia,
        BoxMedicamentoUsoContinuo,
        BoxAnotacoesEnfermagem,
        BoxAtendimentoEnfermagem,
        BoxAvaliacaoAntropometrica,
        BoxCid,
        BoxClassificacaoRisco,
        BoxEscalaComaGlasgow,
        BoxFatorRisco,
        BoxGestante,
        BoxHistoricoAtendimento,
        BoxMedicamentoUso,
        BoxPaciente,
        BoxPacienteObservacao,
        BoxProcedimento,
        BoxProtocolo,
        BoxSinalVital,
        ModalAtestado,
        ModalDeclaracaoComparecimento,
        ModalEncaminhamentoMedico,
        ModalExameSadt,
        ModalImpressao,
        ModalOrientacao,
        ModalPrescricaoInterna,
        ModalAcidenteTrabalho,
        ModalReceitaMedica,
        ModalServicoEnfermagem,
        RkmFormIncrementalTextarea,
        ModalCondicaoSaudePaciente,
        ModalHistoricoAmbulatorio,
    },
    computed: {
        showBoxGestante() {

            if (this.data.form.paciente.isDesconhecido) {

                return false
            }

            return this.data.form.paciente.sexo.id === this.enums.sexo.FEMININO
                && this.data.form.paciente.idade.anos >= 9
                && this.data.form.paciente.idade.anos <= 60
        },
        atendimentoRequired() {
            return (
                !this.data.form.reavaliacao &&
                !this.data.model.queixaPrincipalDuracao &&
                !this.data.model.historia &&
                !this.data.model.exameFisico &&
                !this.data.model.diagnostico &&
                !this.data.model.conduta
            )
        },
        showBoxDataHoraProximaReavaliacao() {

            let centroCustoPara = null

            if (this.data.model.centroCustoIdPara !== null) {

                centroCustoPara = this.data.form.encaminhamentoSetores.find(es => es.id === this.data.model.centroCustoIdPara)
            }

            const hasDesfecho = (this.data.model.solicitouReavaliacao !== null
                                    ? !this.data.model.solicitouReavaliacao
                                    : false)
                                || this.data.model.finalizacao.desfechoId !== null

            const hasPacienteObservacao = this.data.additionals.fromObservacao
            const isCentroCustoParaObservacao =
                centroCustoPara !== null && centroCustoPara.tipo.id === this.enums.setorTipo.OBSERVACAO

            return hasPacienteObservacao && !hasDesfecho
                || (isCentroCustoParaObservacao && !hasPacienteObservacao && !hasDesfecho)
        },
        showBoxPacienteObservacao() {

            return this.data.form.pacienteObservacao !== null
        },
        validations() {
            return {
                proximaReavaliacao: { required: true },
                dataProximaReavaliacao: { required: true },
                horaProximaReavaliacao: { required: true },
                evolucao: {
                    required: this.data.form.reavaliacao,
                    max: 5000,
                },
                atendimento: {
                    queixaPrincipalDuracao: {
                        required: this.atendimentoRequired,
                        max: 5000,
                    },
                    historia: {
                        required: this.atendimentoRequired,
                        max: 5000,
                    },
                    exameFisico: {
                        required: this.atendimentoRequired,
                        max: 5000,
                    },
                    diagnostico: {
                        required: this.atendimentoRequired,
                        max: 5000,
                    },
                    conduta: {
                        required: this.atendimentoRequired,
                        max: 5000,
                    },
                    atendimentoGeral: {
                        required: !this.data.form.reavaliacao && this.data.form.tipoAtendimentoId === this.enums.tipoAtendimento.SIMPLES,
                        max: 5000,
                    },
                    observacoesEnfermagem: { max: 5000 },
                },
                centroCustoIdPara: { required: !this.data.additionals.fromObservacao },
                observacoes: { max: 3000 },
                solicitouReavaliacao: { required: true },
            }
        },
    },
    created() {
        this.repositories = {
            atendimentoMedico: this.getConectaSusRepository('prontoAtendimento.atendimentoMedico')
        }
    },
    data: () => ({
        enums: {
            desfecho,
            documentoTipo,
            setorTipo,
            sexo,
            tipoAplicacao,
            tipoAtendimento,
            tipoPosologia,
            trauma,
            unidadeMedida,
            viaAdministracao,
            tipoExame,
            tipoFormularioAcidenteTrabalho,
            atestadoModelo,
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        getTimeLineClass(item, i) {

            return item.listClass
                || this.data.additionals.timeline[i - 1] && this.data.additionals.timeline[i - 1].listClass
        },
        handleDesfecho() {

            if (this.data.model.finalizacao.desfechoId
                && this.data.model.finalizacao.desfechoId !== this.enums.desfecho.ALTA_MEDICA) {

                    const confirm = async () => {

                        this.data.model.cancelamentoDesfecho = true

                        this.resetDesfecho()

                        this.$nextTick().then(() => this.save())

                        return false
                    }

                    const desfecho = this.data.form.desfechos.find(desfecho => desfecho.id
                        == this.data.model.finalizacao.desfechoId).nome

                    const msg = `Foi atribuído o desfecho <strong>${desfecho}</strong>. Se o atendimento for salvo sem ser
                        finalizado, a atribuição do desfecho será desfeita. Deseja continuar?`

                    this.showConfirmation(msg, confirm)

                    return false
            } else {

                this.save()
            }
        },
        resetDesfecho() {
            this.data.model.finalizacao = Object.assign(this.data.model.finalizacao, {
                desfechoId: null,
                evasao: {
                    motivo: null,
                },
                encaminhamentoHospitalar: {
                    cid: null,
                    leito: null,
                    medicoHospitalar: null,
                    observacoes: null,
                    quarto: null,
                    unidadeId: null,
                    unidadeNome: null,
                    unidadeOutra: false
                },
                obito: {
                    causa: null,
                    cid: null,
                    dataObito: null,
                    declaracao: null,
                    horaObito: null,
                    obitoEm: null,
                }
            })
        },
        save() {

            this.$refs.RkmForm.validate().then(response => {
                if (response === true) {

                    let tipoFormularioAcidenteTrabalho = '';

                    if(this.data.model.acidenteTrabalho) {
                        switch (parseInt(this.data.model.acidenteTrabalho.tipoFormularioAcidenteTrabalho)) {
                            case this.enums.tipoFormularioAcidenteTrabalho.RAAT:
                                tipoFormularioAcidenteTrabalho = 'RAAT'
                                break;
                            case this.enums.tipoFormularioAcidenteTrabalho.CAT:
                                tipoFormularioAcidenteTrabalho = 'CAT'
                                break;
                            default:
                                tipoFormularioAcidenteTrabalho = 'CAT/RAAT'
                        }
                    }

                    if (this.data.model.classificacaoRiscoId === null) {
                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: 'É obrigatório informar a classificação de risco.'
                        })
                    }
                    else if (this.data.form.reavaliacao === false
                        && this.data.model.procedimentos.length === 0
                        && !this.hasProcedimentoParametrizado()) {

                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: 'É obrigatório informar ao menos um procedimento.'
                        })
                    }
                    else if (!this.data.model.solicitouReavaliacao
                        && this.data.model.acidenteTrabalho && this.data.model.acidenteTrabalho.id !== null
                        && this.data.model.acidenteTrabalho.parecerMedico.agenteId === null
                    ) {
                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: tipoFormularioAcidenteTrabalho + ' é obrigatória.'
                        })
                    }
                    else {

                        const isCentroCustoParaExames = this.data.model.centroCustoIdPara !== null && this.data
                            .form.encaminhamentoSetores .find(es => es.id === this.data.model.centroCustoIdPara)
                                .tipo.id === this.enums.setorTipo.EXAMES

                        if (isCentroCustoParaExames) {

                            const hasNewPedidoExameImagem = this.data.model.pedidosExames.id && !!this.data.model
                                .pedidosExames.exames.find(e => e.tipoId === this.enums.tipoExame.IMAGEM)

                            const hasPreviousPedidoExameImagem = !!this.data.form.pedidosExames
                                .find(p => p.exames.find(e => e.tipoId === this.enums.tipoExame.IMAGEM))

                            const hasExameImagem = hasNewPedidoExameImagem || hasPreviousPedidoExameImagem

                            if (!hasExameImagem) {

                                this.createNotification({
                                    type: 'alert',
                                    color: 'danger',
                                    title: 'Ação não permitida',
                                    message: 'Não há exame de imagem solicitado. Solicite um ou mais para encaminhar a este setor.'
                                })

                                return
                            }
                        }

                        this.setLoading()

                        let request = {
                            plantaoId: this.plantaoId,
                            dataHoraProximaReavaliacao: this.getDataHoraProximaReavaliacao(),
                            atendimentoId: this.data.model.atendimentoId,
                            classificacaoRiscoId: this.data.model.classificacaoRiscoId,
                            alergias: this.data.model.alergias.map(item => {
                                return {
                                    alergiaId: item.alergia.id,
                                    criticidadeId: item.alergia.criticidade.id,
                                    reacoesObservacoes: item.alergia.reacoesObservacoes,
                                }
                            }),
                            medicamentosUsoContinuo: this.data.model.medicamentosUsoContinuo.map(item => {
                                return {
                                    medicamentoId: item.medicamento.id,
                                }
                            }),
                            medicamentosUso: this.data.model.medicamentosUso,
                            sinaisVitais: this.data.model.sinaisVitais.map(item => {
                                return {
                                    pa: item.pa || null,
                                    paUnidadeMedidaId: item.pa ? item.paUnidadeMedidaId : null,
                                    glicemia: item.glicemia || null,
                                    temperatura: item.temperatura ? this.unMaskTemperaturaMMDG(item.temperatura) : null,
                                    pulso: item.pulso || null,
                                    respiracao: item.respiracao || null,
                                    saturacao: item.saturacao || null,
                                    intensidadeDor: item.intensidadeDor || null,
                                    cintura: item.cintura || null,
                                }
                            }),
                            escalaComaGlasgow: this.data.model.escalasComaGlasgow.map(item => {
                                return {
                                    aberturaOcularId: item.aberturaOcular.id,
                                    respostaVerbalId: item.respostaVerbal.id,
                                    respostaMotoraId: item.respostaMotora.id,
                                    reatividadePupilarId : item.reatividadePupilar.id,
                                    observacao: item.observacoes
                                }
                            }),
                            gestacao: {
                                gestante: this.data.model.gestacao.gestante,
                                dum: this.data.model.gestacao.dum ? moment(this.data.model.gestacao.dum).format('YYYY-MM-DD') : null,
                            },
                            fatoresRisco: this.data.model.fatoresRisco,
                            fatorRiscoOutro: this.data.model.fatorRiscoOutro,
                            evolucao: this.data.model.evolucao,
                            atendimentoGeral: this.data.model.atendimentoGeral,
                            queixaPrincipalDuracao: this.data.model.queixaPrincipalDuracao,
                            historia: this.data.model.historia,
                            exameFisico: this.data.model.exameFisico,
                            diagnostico: this.data.model.diagnostico,
                            conduta: this.data.model.conduta,
                            observacoesEnfermagem: this.data.model.observacoesEnfermagem,
                            observacoes: this.data.model.observacoes,
                            dorToracicaProtocoloFinalizado: this.data.model.dorToracicaProtocoloFinalizado,
                            dorToracicaProtocoloTemProsseguimento: this.data.model.dorToracicaProtocoloTemProsseguimento,
                            dorToracicaResultadoEletrocardiograma: this.data.model.dorToracicaResultadoEletrocardiograma,
                            dorToracicaTemContraIndicacaoTrombolitico: this.data.model.dorToracicaTemContraIndicacaoTrombolitico,
                            dorToracicaContraIndicacaoTromboliticoMotivo: this.data.model.dorToracicaContraIndicacaoTromboliticoMotivo,
                            dorToracicaSinaisInstabilidade: this.data.model.dorToracicaSinaisInstabilidade,
                            cidId: this.data.model.cid.id,
                            procedimentos: this.data.model.procedimentos.map(item => {
                                return {
                                    procedimentoId: item.procedimento.id,
                                    cidPrincipalId: item.cidPrincipal ? item.cidPrincipal.id : null,
                                    quantidade: item.quantidade,
                                    isParametrizado: item.isParametrizado,
                                }
                            }),
                            centroCustoIdPara: this.data.model.centroCustoIdPara,
                            desfecho: {
                                id: this.getDesfechoId(),
                            },
                            encaminhamentos: this.data.model.encaminhamentos.map(item => {
                                return {
                                    urgente: item.urgente,
                                    ocupacaoId: item.ocupacao.id,
                                    motivo: item.motivo,
                                    observacoes: item.observacoes,
                                }
                            }),
                            servicosEnfermagem: this.data.model.servicosEnfermagem.map(item => {
                                return {
                                    procedimentoId: item.procedimento.id,
                                    observacoes: item.observacoes,
                                }
                            }),
                            declaracoesComparecimento: this.data.model.declaracoesComparecimento.map(item => {
                                const de = moment(moment(item.dataDe).format('YYYY-MM-DD') + ' ' + item.horaDe).format('YYYY-MM-DD HH:mm')
                                const ate = moment(moment(item.dataAte).format('YYYY-MM-DD') + ' ' + item.horaAte).format('YYYY-MM-DD HH:mm')
                                let documentoNumero = item.documentoNumero

                                switch (item.documentoTipoId) {
                                    case this.enums.documentoTipo.CPF:
                                        documentoNumero = documentoNumero ? this.unMaskCpf(documentoNumero) : null
                                        break;

                                    case this.enums.documentoTipo.CNS:
                                        documentoNumero = documentoNumero ? this.unMaskCns(documentoNumero) : null
                                        break;
                                }

                                return {
                                    tipoId: item.tipoId,
                                    nome: item.nome,
                                    documentoTipoId: item.documentoTipoId,
                                    documentoNumero: documentoNumero,
                                    de: de,
                                    ate: ate,
                                    motivo: item.motivo,
                                }
                            }),
                            prescricoesInternas: this.data.model.prescricoesInternas.map(item => {
                                return {
                                    doseUnica: item.doseUnica,
                                    frequenciaQuantidadeVezes: item.frequenciaQuantidadeVezes,
                                    frequenciaTempo: item.frequenciaTempo,
                                    frequenciaMedidaTempoId: item.frequenciaMedidaTempoId,
                                    observacoes: item.observacoes,
                                    medicamentos: item.medicamentos.map(medicamento => {
                                        return {
                                            medicamentoId: medicamento.medicamento.id,
                                            unidadeMedidaId: medicamento.unidadeMedidaId,
                                            quantidade: medicamento.quantidade,
                                            administracao: medicamento.administracao,
                                        }
                                    }),
                                }
                            }),
                            orientacoes: this.data.model.orientacoes,
                            receitasMedicasAssinadas: this.data.model.receitasMedicasAssinadas,
                        }

                        request.receitaMedica = {
                            isAssinada: this.data.model.receitaMedica.isAssinada,
                            medicamentos: []
                        }

                        if (this.data.model.receitaMedica.medicamentos.length > 0) {

                            request.receitaMedica.medicamentos = this.data.model.receitaMedica.medicamentos
                                .map(item => {
                                    let data = {
                                        prescricaoManual: item.prescricaoManual ? item.prescricaoManual : null,
                                        medicamentoId: null,
                                        medicamentoManual: null,
                                    }

                                    if (!item.prescricaoManual) {
                                        data.medicamentoId = item.medicamentoCadastrado == 1 ? item.medicamento.id : null
                                        data.medicamentoManual = item.medicamentoCadastrado == 2 ? item.medicamentoManual : null
                                    }

                                    if (item.viaAdministracao.id === this.enums.viaAdministracao.OUTRAS) {

                                        data.viaAdministracaoOutras = item.viaAdministracaoOutras
                                    }

                                    switch (item.tipoAplicacao.id) {

                                        case this.enums.tipoAplicacao.DOSE_UNICA: {

                                            return {
                                                ...data,
                                                tipoAplicacao: 'dose_unica',
                                                quantidade: item.quantidade,
                                                recomendacoes: item.recomendacoes,
                                            }
                                        }
                                        case this.enums.tipoAplicacao.USO_CONTINUO: {

                                            data = {
                                                ...data,
                                                tipoAplicacao: 'uso_continuo',
                                                quantidade: item.quantidade,
                                                tipoPosologia: item.tipoPosologia === this.enums.tipoPosologia.FREQUENCIA
                                                    ? 'frequencia'
                                                    : 'turno',
                                                recomendacoes: item.recomendacoes,
                                            }

                                            return item.tipoPosologia === this.enums.tipoPosologia.FREQUENCIA
                                                ? {
                                                    ...data,
                                                    intervaloTempo: item.intervaloTempo,
                                                    intervaloMedidaTempoId: item.intervaloMedidaTempo.id,
                                                }
                                                : {
                                                    ...data,
                                                    turnoId: item.turno.id
                                                }
                                        }
                                        case this.enums.tipoAplicacao.LIVRE: {

                                            return {
                                                ...data,
                                                tipoAplicacao: 'livre',
                                                descricao: item.descricao,
                                            }
                                        }
                                        default: {

                                            data = {
                                                ...data,
                                                tipoAplicacao: 'padrao',
                                                tipoPosologia: item.tipoPosologia === this.enums.tipoPosologia.FREQUENCIA
                                                    ? 'frequencia'
                                                    : 'turno',
                                                quantidade: item.quantidade,
                                                duranteTempo: item.duranteQuantidade,
                                                duranteMedidaTempoId: item.duranteMedidaTempo.id,
                                                recomendacoes: item.recomendacoes,
                                            }

                                            return item.tipoPosologia === this.enums.tipoPosologia.FREQUENCIA
                                                ? {
                                                    ...data,
                                                    intervaloTempo: item.intervaloTempo,
                                                    intervaloMedidaTempoId: item.intervaloMedidaTempo.id,
                                                }
                                                : {
                                                    ...data,
                                                    turnoId: item.turno.id
                                                }
                                        }
                                    }
                                })
                        }

                        if (request.desfecho.id === desfecho.EVASAO) {

                            request.desfecho = Object.assign(request.desfecho, {
                                evasao: {
                                    motivo: this.data.model.finalizacao.evasao.motivo,
                                },
                            })

                        } else if (request.desfecho.id === desfecho.ENCAMINHAMENTO_HOSPITALAR) {

                            request.desfecho = Object.assign(request.desfecho , {
                                encaminhamentoHospitalar: {
                                    unidadeId: this.data.model.finalizacao.encaminhamentoHospitalar.unidadeId,
                                    unidadeNome: this.data.model.finalizacao.encaminhamentoHospitalar.unidadeNome,
                                    quarto: this.data.model.finalizacao.encaminhamentoHospitalar.quarto,
                                    leito: this.data.model.finalizacao.encaminhamentoHospitalar.leito,
                                    cidId: this.data.model.finalizacao.encaminhamentoHospitalar.cid.id,
                                    medicoHospitalar: this.data.model.finalizacao.encaminhamentoHospitalar.medicoHospitalar,
                                    observacoes: this.data.model.finalizacao.encaminhamentoHospitalar.observacoes,
                                },
                            })

                        } else if (request.desfecho.id === desfecho.OBITO) {

                            request.desfecho = Object.assign(request.desfecho , {
                                obito: {
                                    obitoEm: moment(
                                        moment(
                                            this.data.model.finalizacao.obito.dataObito).format('YYYY-MM-DD') + ' ' +
                                            this.data.model.finalizacao.obito.horaObito
                                        ).format('DD/MM/YYYY HH:mm'),
                                    declaracao: this.data.model.finalizacao.obito.declaracao,
                                    causa: this.data.model.finalizacao.obito.causa,
                                    cidId: this.data.model.finalizacao.obito.cid.id
                                },
                            })
                        }

                        request.cancelamentoDesfecho = this.data.model.cancelamentoDesfecho

                        if (this.data.model.avaliacaoAntropometrica.massa || this.data.model.avaliacaoAntropometrica.altura) {
                            request.avaliacaoAntropometrica = {
                                massa: this.data.model.avaliacaoAntropometrica.massa,
                                massaUnidadeMedidaId: this.data.model.avaliacaoAntropometrica.massaUnidadeMedidaId,
                                alturaTipoMedidaId: this.data.model.avaliacaoAntropometrica.alturaTipoMedidaId,
                                altura: this.data.model.avaliacaoAntropometrica.altura,
                                alturaUnidadeMedidaId: this.data.model.avaliacaoAntropometrica.alturaUnidadeMedidaId,
                            }
                        }

                        if (this.data.model.pedidosExames.id) {
                            request.pedidosExames = {
                                urgente: this.data.model.pedidosExames.urgente,
                                justificativa: this.data.model.pedidosExames.justificativa,
                                exames: this.data.model.pedidosExames.exames.map(item => {
                                    return {
                                        exameId: item.id,
                                        observacaoMedico: item.observacaoMedico || null,
                                    }
                                }),
                            }
                        }

                        const atestado = this.data.model.atestado || {}
                        const hasAtestadoData = !!(
                            atestado.documentoTipoId
                            || atestado.documentoNumero
                            || atestado.nome
                        )
                        const hasModeloId =
                            atestado.modeloId !== null
                            && atestado.modeloId !== undefined
                            && atestado.modeloId !== ''
                        const isModeloPadrao = Number(atestado.modeloId) === this.enums.atestadoModelo.PADRAO
                        const isModeloQuantidadeHoras = Number(atestado.modeloId) === this.enums.atestadoModelo.QUANTIDADE_HORAS
                        const hasDias = atestado.dias !== null && atestado.dias !== undefined && atestado.dias !== ''
                        const hasHorarioQuantidadeHoras = !!(atestado.horarioInicial && atestado.quantidadeHoras)
                        const canSendAtestado = hasAtestadoData
                            && hasModeloId
                            && (!isModeloPadrao || hasDias)
                            && (!isModeloQuantidadeHoras || hasHorarioQuantidadeHoras)

                        if (canSendAtestado) {
                            let documentoNumero = atestado.documentoNumero
                            const horarioInicial = atestado.horarioInicial
                                ? moment(atestado.horarioInicial, ['HH:mm:ss', 'HH:mm']).format('HH:mm')
                                : null

                            switch (parseInt(atestado.documentoTipoId)) {
                                case this.enums.documentoTipo.CPF:
                                    documentoNumero = documentoNumero ? this.unMaskCpf(documentoNumero) : null
                                break;

                                case this.enums.documentoTipo.CNS:
                                    documentoNumero = documentoNumero ? this.unMaskCns(documentoNumero) : null
                                break;
                            }

                            request.atestado = {
                                nomeAcompanhante: atestado.paciente == 0 ? atestado.nome : null,
                                paciente: atestado.paciente,
                                imprimeCid: atestado.imprimeCid,
                                dias: atestado.dias,
                                modeloId: atestado.modeloId,
                                horarioInicial: horarioInicial,
                                quantidadeHoras: atestado.quantidadeHoras,
                                documentoTipoId: atestado.documentoTipoId,
                                documentoNumero: documentoNumero,
                            }
                        }

                        if (this.data.model.acidenteTrabalho && this.data.model.acidenteTrabalho.parecerMedico.agenteId) {
                            request.raat = {
                                id: this.data.model.acidenteTrabalho.id,
                                tipoFormularioAcidenteTrabalho: this.data.model.acidenteTrabalho.tipoFormularioAcidenteTrabalho,
                                agenteId: this.data.model.acidenteTrabalho.parecerMedico.agenteId,
                                partesCorpoAtingidas: this.data.model.acidenteTrabalho.parecerMedico.partesCorpoAtingidas,
                                parteCorpoAtingidaOutras: this.data.model.acidenteTrabalho.parecerMedico.parteCorpoAtingidaOutras,
                                causasAcidente: this.data.model.acidenteTrabalho.parecerMedico.causasAcidente,
                                causaAcidenteOutras: this.data.model.acidenteTrabalho.parecerMedico.causaAcidenteOutras,
                                diagnosticos: this.data.model.acidenteTrabalho.parecerMedico.diagnosticos,
                                diagnosticoOutros: this.data.model.acidenteTrabalho.parecerMedico.diagnosticoOutros,
                                observacao: this.data.model.acidenteTrabalho.parecerMedico.observacao,
                                classificacaoInicialId: this.data.model.acidenteTrabalho.parecerMedico.classificacaoInicialId,
                                afastado: this.data.model.acidenteTrabalho.parecerMedico.afastado,
                                pacienteSofreuAcidenteTrabalho: this.data.model.acidenteTrabalho.parecerMedico.pacienteSofreuAcidenteTrabalho,
                                pacientePortadorDoencaOcupacional: this.data.model.acidenteTrabalho.parecerMedico.pacientePortadorDoencaOcupacional,
                                pacientePrecisaInternacao: this.data.model.acidenteTrabalho.parecerMedico.pacientePrecisaInternacao,
                                diasAfastamento: this.data.model.acidenteTrabalho.parecerMedico.diasAfastamento,
                                acidenteTipoId: this.data.model.acidenteTrabalho.acidenteTipoId,
                            }
                        }

                        this.repositories.atendimentoMedico.store(string.camelObjectKeysToSnakeRecursive(request))
                            .then(({ data }) => {

                                const finish = () => {

                                    this.$refs.ModalImpressao.open(
                                        data.impressoes,
                                        data.atendimentoId,
                                        () => this.$emit('save')
                                    )
                                }

                                if (data.assinar) {

                                    this.afterSign = faaAssinada => {

                                        return this.repositories.atendimentoMedico.storeFaaAssinada(data.id, {
                                            faa_assinada: faaAssinada
                                        })
                                    }

                                    this.onSign = () => finish()
                                    this.onSkipSign = () => finish()

                                    this.askToSignAtendimento(
                                        async () => this.signFaa(
                                            data.atendimentoId,
                                            [{ tipo: 'medico', id: data.id }],
                                            `faa-${data.faaId}-atendimento-medico-${data.id}`
                                        ),
                                        async () => finish()
                                    )
                                }
                                else {

                                    this.showSaveNotification()

                                    finish()
                                }
                            })
                            .catch(this.handleError)
                            .finally(() => this.unsetLoading())
                    }
                }
            })
        },
        hasProcedimentoParametrizado() {

            if (this.data.form.procedimentos.length === 0) {

                return false
            }

            return this.data.form.procedimentos.find(p => p.procedimento.isParametrizado)
        },
        getDataHoraProximaReavaliacao() {

            if (this.data.model.dataProximaReavaliacao === null || this.data.model.horaProximaReavaliacao === null) {

                return null
            }

            return moment(
                    moment(
                        this.data.model.dataProximaReavaliacao).format('YYYY-MM-DD') + ' ' +
                        this.data.model.horaProximaReavaliacao
                    ).format('DD/MM/YYYY HH:mm')
        },
        getDesfechoId() {

            if (this.data.model.finalizacao.desfechoId !== null) {

                return this.data.model.finalizacao.desfechoId
            }

            if (this.data.form.pacienteObservacao === null) {

                return this.data.model.solicitouReavaliacao ? null : desfecho.ALTA_MEDICA
            }

            return null
        },
        handleSolicitouReavaliacao() {

            this.data.model.finalizacao.desfechoId = null;

            if (this.data.model.solicitouReavaliacao === false) {

                this.data.model.finalizacao.desfechoId = this.enums.desfecho.ALTA_MEDICA;
            }
        }
    },
    mixins: [error, repository, unmask, prontoAtendimentoAssinaturaEletronica],
    props: {
        data: { type: Object, required: true },
        plantaoId: { type: [String, Number], required: true },
        color: { type: String, default: '' },
    },
    watch: {
        'data.model.finalizacao.desfechoId': function () {

            if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.ALTA_MEDICA) {

                this.data.model.solicitouReavaliacao = false
            }
        },
        'data.model.proximaReavaliacao': function () {

            if (this.data.model.proximaReavaliacao === false) {

                this.data.model.dataProximaReavaliacao = null
            } else {

                this.data.model.dataProximaReavaliacao = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
            }
            this.data.model.horaProximaReavaliacao = null
        },
        'data.model.solicitouReavaliacao': function () {

            if (this.data.model.solicitouReavaliacao === false) {

                this.data.model.dataProximaReavaliacao = null
            } else {

                this.data.model.dataProximaReavaliacao = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
            }
            this.data.model.horaProximaReavaliacao = null
        }
    }
}
</script>